import { useState, useEffect, useCallback } from 'react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import {
  DollarSign,
  UtensilsCrossed,
  Wallet,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader2,
  AlertTriangle,
  PieChart as PieChartIcon,
  BarChart3,
  ClipboardList,
} from 'lucide-react'
import { ApiService } from '@/services/api'
import { useCurrency } from '@/hooks/useCurrency'
import { formatDateForInput, parseDate, createDateRange } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'

// Same palette as the Inventory app's own P&L Reports page, so a restaurant
// looks the same whichever app it's viewed from.
const COLORS = {
  foodCost: '#2a78d6',
  rent: '#1baf7a',
  utilities: '#eda100',
  salary: '#008300',
  other: '#e87ba4',
  netProfit: '#4a3aa7',
  netLoss: '#d03b3b',
  waste: '#d03b3b',
}

interface PnlData {
  period: 'daily' | 'monthly'
  date: string
  summary: {
    netSales: number
    foodCost: number
    totalExpenses: number
    totalCost: number
    netProfit: number
  }
  breakdown: {
    foodCost: number
    rent: number
    utilities: number
    salary: number
    other: number
    waste: number
  }
}

export default function PnlReportScreen() {
  const { formatCurrency } = useCurrency()
  const { currentRestaurant } = useRestaurant()
  const [period, setPeriod] = useState<'daily' | 'monthly'>('daily')
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [data, setData] = useState<PnlData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const anchorDate = dateRange.from

  const loadReport = useCallback(async () => {
    if (!currentRestaurant?._id) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getPnlReport(currentRestaurant._id, period, formatDateForInput(anchorDate))
      if (response.success) setData(response.data)
      else setError(response.message || 'Failed to load P&L report')
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as any).response?.data?.message || 'Failed to load P&L report'
        : 'Failed to load P&L report'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [currentRestaurant?._id, period, anchorDate])

  useEffect(() => {
    if (currentRestaurant?._id) loadReport()
  }, [currentRestaurant?._id, period, anchorDate, loadReport])

  const handleDateChange = (value: string) => {
    const newDate = parseDate(value)
    setDateRange(createDateRange(newDate, newDate))
  }

  const dateLabel = period === 'daily'
    ? anchorDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : anchorDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2 text-muted-foreground">Loading P&amp;L report...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl sm:text-2xl font-bold">P&amp;L Report</h1>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-2 py-[10px]" onClick={loadReport}>
              <RefreshCw className="h-4 w-4 mr-1" /> Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const { netSales, foodCost, totalExpenses, totalCost, netProfit } = data?.summary ?? {
    netSales: 0, foodCost: 0, totalExpenses: 0, totalCost: 0, netProfit: 0
  }
  const { rent, utilities, salary, other, waste } = data?.breakdown ?? {
    foodCost: 0, rent: 0, utilities: 0, salary: 0, other: 0, waste: 0
  }
  const isLoss = netProfit < 0

  const pct = (amount: number) => netSales > 0 ? (amount / netSales) * 100 : 0
  const pctLabel = (amount: number) => `${Math.round(pct(amount))}%`

  // Waste and Other (Supplier Payment) only show up when they're actually
  // non-zero for the period — same as the Inventory app's own report.
  const breakdownRows = [
    { key: 'foodCost', label: 'Food Cost', amount: foodCost, color: COLORS.foodCost },
    { key: 'rent', label: 'Rent', amount: rent, color: COLORS.rent },
    { key: 'utilities', label: 'Utilities', amount: utilities, color: COLORS.utilities },
    { key: 'salary', label: 'Salary', amount: salary, color: COLORS.salary },
    ...(waste > 0 ? [{ key: 'waste', label: 'Waste', amount: waste, color: COLORS.waste }] : []),
    ...(other > 0 ? [{ key: 'other', label: 'Supplier Payment', amount: other, color: COLORS.other }] : []),
    { key: 'netProfit', label: isLoss ? 'Net Loss' : 'Net Profit', amount: netProfit, color: isLoss ? COLORS.netLoss : COLORS.netProfit },
  ]

  const pieData = breakdownRows.filter(r => r.amount > 0).map(r => ({ name: r.label, value: r.amount, color: r.color }))
  const maxLineAmount = Math.max(...breakdownRows.map(r => Math.abs(r.amount)), 1)

  const statementRows = [
    { key: 'netSales', label: 'Net Sales', amount: netSales, indent: false, bg: 'bg-muted/40', text: 'font-semibold' },
    { key: 'foodCost', label: 'Cost of Food Used', amount: foodCost, indent: false, bg: '', text: 'font-medium' },
    { key: 'rent', label: 'Rent', amount: rent, indent: true, bg: '', text: 'text-muted-foreground' },
    { key: 'utilities', label: 'Utilities (Electricity, Water, Gas)', amount: utilities, indent: true, bg: '', text: 'text-muted-foreground' },
    { key: 'salary', label: 'Salary', amount: salary, indent: true, bg: '', text: 'text-muted-foreground' },
    ...(waste > 0 ? [{ key: 'waste', label: 'Waste', amount: waste, indent: true, bg: '', text: 'text-muted-foreground' }] : []),
    ...(other > 0 ? [{ key: 'other', label: 'Supplier Payment', amount: other, indent: true, bg: '', text: 'text-muted-foreground' }] : []),
    { key: 'totalCost', label: 'Total Cost (Food + Expenses)', amount: totalCost, indent: false, bg: 'bg-destructive/5', text: 'font-semibold text-destructive' },
    {
      key: 'netProfit',
      label: isLoss ? 'Net Loss' : 'Net Profit',
      amount: netProfit,
      indent: false,
      bg: isLoss ? 'bg-destructive/5' : 'bg-green-500/10',
      text: `font-semibold ${isLoss ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`
    },
  ]

  const formulaParts = ['Cost of Food Used', 'Rent', 'Utilities', 'Salary']
  if (waste > 0) formulaParts.push('Waste')
  if (other > 0) formulaParts.push('Supplier Payment')

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">P&amp;L Report</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Food cost, expenses and profit — {dateLabel}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border overflow-hidden h-9 flex-shrink-0">
            {(['daily', 'monthly'] as const).map((p, i) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={[
                  'px-3 text-xs font-medium capitalize transition-colors focus:outline-none',
                  i > 0 ? 'border-l' : '',
                  period === p ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted',
                ].join(' ')}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          <Label htmlFor="pnl-date" className="sr-only">Date</Label>
          <Input
            id="pnl-date"
            type="date"
            value={formatDateForInput(anchorDate)}
            onChange={(e) => handleDateChange(e.target.value)}
            className="h-9 flex-1 min-w-0 sm:flex-none sm:w-auto"
          />
          <Button onClick={loadReport} variant="outline" size="icon" className="h-9 w-9 flex-shrink-0" disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Sales</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(netSales)}</div>
            <p className="text-xs text-muted-foreground mt-1">{dateLabel}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Food Cost</CardTitle>
            <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(foodCost)}</div>
            <p className="text-xs text-muted-foreground mt-1">{pctLabel(foodCost)} of sales</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(totalExpenses)}</div>
            <p className="text-xs text-muted-foreground mt-1">{pctLabel(totalExpenses)} of sales</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
            {isLoss ? <TrendingDown className="h-4 w-4 text-destructive" /> : <TrendingUp className="h-4 w-4 text-muted-foreground" />}
          </CardHeader>
          <CardContent>
            <div className={`text-xl sm:text-2xl font-bold ${isLoss ? 'text-destructive' : ''}`}>{formatCurrency(netProfit)}</div>
            <p className="text-xs text-muted-foreground mt-1">{pctLabel(Math.abs(netProfit))} margin</p>
          </CardContent>
        </Card>
      </div>

      {/* Cost Composition + Line-Item Breakdown */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <PieChartIcon className="h-4 w-4 text-muted-foreground" />
              Cost Composition
            </CardTitle>
            <CardDescription>Share of net sales</CardDescription>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No data for this period.</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any, name: any) => [formatCurrency(Number(value) || 0), String(name ?? '')] as [string, string]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-1.5 mt-2 text-sm">
                  {breakdownRows.map(row => (
                    <div key={row.key} className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }} />
                        {row.label}
                      </span>
                      <span className="font-medium">{formatCurrency(row.amount)} · {pctLabel(row.amount)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              Line-Item Breakdown
            </CardTitle>
            <CardDescription>Each line as % of net sales</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {breakdownRows.map(row => (
              <div key={row.key}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium">{row.label}</span>
                  <span className="text-muted-foreground">{formatCurrency(row.amount)} · {pctLabel(row.amount)}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, Math.max(0, (Math.abs(row.amount) / maxLineAmount) * 100))}%`,
                      backgroundColor: row.color
                    }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Statement */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
            Detailed Statement
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Desktop table */}
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Line</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">% of Sales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statementRows.map(row => (
                  <TableRow key={row.key} className={row.bg}>
                    <TableCell className={`${row.indent ? 'pl-8' : ''} ${row.text}`}>{row.label}</TableCell>
                    <TableCell className={`text-right ${row.text}`}>{formatCurrency(row.amount)}</TableCell>
                    <TableCell className={`text-right ${row.text}`}>
                      {row.key === 'netSales' ? '100%' : pctLabel(row.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards — a 3-column table squeezes "Utilities (Electricity,
              Water, Gas)" into an unreadable wrap; give the label full width
              instead, with amount/% stacked to the right. */}
          <div className="lg:hidden divide-y rounded-lg border overflow-hidden">
            {statementRows.map(row => (
              <div key={row.key} className={`flex items-start justify-between gap-3 px-3 py-2.5 ${row.bg}`}>
                <span className={`text-sm ${row.indent ? 'pl-3' : ''} ${row.text}`}>{row.label}</span>
                <span className={`text-sm text-right flex-shrink-0 ${row.text}`}>
                  <div>{formatCurrency(row.amount)}</div>
                  <div className="text-xs opacity-80">{row.key === 'netSales' ? '100%' : pctLabel(row.amount)}</div>
                </span>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            Net Profit = Net Sales − ({formulaParts.join(' + ')}). Food cost is the
            recipe-cost based figure the app already tracks in the Food Cost Report. Salary counts actual
            payments made in the period (Salary Manage), not an accrual. Amounts in {currentRestaurant?.currency || 'INR'}.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
